import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material'
import {
  HoldAuditEventDto,
  HoldAuditType,
  HoldReaction,
  HoldRestrictionType,
  HoldStatus,
} from '@planning/app/api'
import { ItemWithLabel } from '@planning/components/hold/atoms/ItemWithLabel'
import { getHoldAuditEventLabel, getHoldStatusColor } from '@planning/components/hold/hold.utils'
import { HoldOverrideDialog } from '@planning/components/hold/molecules/HoldOverrideDialog'
import { OrderHoldsSectionStore } from '@planning/components/hold/organisms/stores/OrderHoldsSectionStore'
import { HoldRestrictionChip } from '@planning/pages/HoldTypes/atoms/HoldRestrictionChip'
import { useTranslate } from '@tolgee/react'
import { ChevronDownIcon, ColoredLabel, OptionsButton, useMinimalsTheme } from '@tom-ui/ui'
import { formatDateTime } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'

type GroupedEvent = {
  type?: HoldAuditType
  at?: string
  responsibleUserName?: string | null
  reason?: string | null
  overrideTypes: (HoldRestrictionType | undefined)[]
}

const groupOverrideEvents = (events: HoldAuditEventDto[]): GroupedEvent[] => {
  const result: GroupedEvent[] = []
  const seenKeys = new Map<string, GroupedEvent>()

  for (const event of events) {
    if (event.type === HoldAuditType.Override) {
      const key = `${event.at ?? ''}-${event.responsibleUserName ?? ''}-${event.reason ?? ''}`
      const existing = seenKeys.get(key)
      if (existing) {
        existing.overrideTypes.push(event.overrideType)
      } else {
        const group: GroupedEvent = {
          type: event.type,
          at: event.at,
          responsibleUserName: event.responsibleUserName,
          reason: event.reason,
          overrideTypes: [event.overrideType],
        }
        seenKeys.set(key, group)
        result.push(group)
      }
    } else {
      result.push({
        type: event.type,
        at: event.at,
        responsibleUserName: event.responsibleUserName,
        reason: event.reason,
        overrideTypes: [],
      })
    }
  }

  return result
}

export const HoldsAuditTrail = observer(
  ({ store, containerNumber }: { store: OrderHoldsSectionStore; containerNumber?: string }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const [resolveComment, setResolveComment] = useState('')

    return (
      <>
        {store.holdsAuditTrail.map(item => (
          <Accordion
            key={item.holdId}
            variant='elevation'
            disableGutters
            elevation={0}
            sx={{
              boxShadow: theme.shadows[4],
              '&:before': { display: 'none' },
              borderRadius: theme.customRadius.medium,
              marginBottom: theme.customSpacing.m,
            }}
          >
            <AccordionSummary
              sx={{ flexDirection: 'row-reverse', gap: theme.customSpacing.m }}
              expandIcon={<ChevronDownIcon />}
              aria-controls='panel1a-content'
              id='panel1a-header'
            >
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                width='100%'
                px={theme.customSpacing.s}
                gap={theme.customSpacing.m}
              >
                <Box
                  display='grid'
                  gridTemplateColumns='1fr 3fr'
                  gap={theme.customSpacing.l}
                  sx={{ flex: '0 0 40%', minWidth: 0 }}
                >
                  <ItemWithLabel label={t('type', 'Type')} value={item.holdTypeName} />
                  <ItemWithLabel
                    label={t('description', 'Description')}
                    value={item.holdTypeDescription}
                  />
                </Box>
                <Box
                  display='grid'
                  gridTemplateColumns='repeat(4, 1fr)'
                  gap={theme.customSpacing.s}
                  width={{ md: '50%', lg: '45%', xl: '35%' }}
                  alignItems='center'
                  justifyItems='flex-start'
                  flexGrow={{ md: 1, lg: 0 }}
                >
                  {[
                    HoldRestrictionType.Entry,
                    HoldRestrictionType.Exit,
                    HoldRestrictionType.Yard,
                  ].map(type => {
                    const restriction = item.restrictions?.find(r => r.restrictionType === type)
                    return (
                      <Box key={type}>
                        {item?.restrictions?.length !== 0 ? (
                          restriction ? (
                            <HoldRestrictionChip
                              restriction={restriction.restrictionType}
                              reaction={restriction.reaction}
                              fontSize='14px'
                            />
                          ) : (
                            <>{'—'}</>
                          )
                        ) : null}
                      </Box>
                    )
                  })}
                  <Box display='flex' alignItems='center' gap={theme.customSpacing.s}>
                    <ColoredLabel
                      sx={{ height: theme.customSpacing.l }}
                      color={getHoldStatusColor(item.holdStatus)}
                      label={
                        item.holdStatus === HoldStatus.Active
                          ? t('active', 'Active')
                          : item.holdStatus === HoldStatus.Resolved
                            ? t('resolved', 'Resolved')
                            : t('unknown', 'Unknown')
                      }
                    />
                    {item.holdStatus === HoldStatus.Active &&
                      item.restrictions?.some(r => r.reaction === HoldReaction.Block) && (
                        <OptionsButton
                          tooltip={t('moreOptions', 'More options')}
                          position='right'
                          options={[
                            {
                              label: t('override', 'Override'),
                              onClick: () => store.openOverrideDialog(item.holdId!),
                              testId: 'override-hold-btn',
                            },
                          ]}
                        />
                      )}
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {!item.events?.length ? (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  px={theme.customSpacing.xxl}
                  py={theme.customSpacing.s}
                >
                  {t('holds.auditTrail.noEvents', 'No audit events recorded')}
                </Typography>
              ) : (
                <Grid container paddingX={theme.customSpacing.xxl} size={6}>
                  {groupOverrideEvents(item.events ?? []).map((event, index) => {
                    const { on, by } = getHoldAuditEventLabel(event.type)
                    const isOverride = event.type === HoldAuditType.Override
                    return (
                      <Fragment key={`${event.at}-${event.type}-${index}`}>
                        <Grid size={3}>
                          <ItemWithLabel label={on} value={formatDateTime(event.at)} />
                        </Grid>
                        <Grid size={3}>
                          <ItemWithLabel label={by} value={event.responsibleUserName} />
                        </Grid>
                        <Grid size={isOverride ? 3 : 6}>
                          <ItemWithLabel
                            label={t('description', 'Description')}
                            value={event.reason}
                          />
                        </Grid>
                        {isOverride && (
                          <Grid size={3}>
                            <ItemWithLabel
                              label={t('overrideType', 'Override Type')}
                              value={event.overrideTypes.filter(Boolean).join(', ')}
                            />
                          </Grid>
                        )}
                      </Fragment>
                    )
                  })}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        <HoldOverrideDialog
          open={store.isOverrideDialogOpen}
          onClose={store.closeOverrideDialog}
          holdTypeName={
            store.holdsAuditTrail.find(h => h.holdId === store.overrideHoldId)?.holdTypeName!
          }
          containerNumber={containerNumber}
          restrictions={store.holdsAuditTrail
            .find(h => h.holdId === store.overrideHoldId)
            ?.restrictions?.filter(r => r.reaction === HoldReaction.Block)}
          onConfirm={(operationTypes, reason) =>
            store.override(store.overrideHoldId!, operationTypes, reason)
          }
        />
      </>
    )
  },
)
